/**
 * Hook: POST /api/t/{slug}/notes — append-only create (Batch 30j-6).
 *
 * Optimistic prepend into the `useNotes` infinite-query cache via
 * `setQueryData`, with rollback on error. Per Architect §5.3 (AR-5)
 * page-shape contract:
 *
 *   - mutate ONLY `pages[0].items` (prepend);
 *   - preserve `pageParams`, `pages.length`, `next_cursor`, `has_more`;
 *   - cold cache (`pages` undefined or empty) is a no-op on `onMutate`;
 *   - `onSuccess` rebuilds a single-page cache if cold, replaces the
 *     optimistic row otherwise (match by `id === optimisticId`).
 *
 * Sec HARD-7: append-only enforcement. NO `useDeleteNote` /
 * `useUpdateNote` / `usePatchNote` — backend mounts only GET + POST.
 *
 * Sec HARD-8: optimistic write constructs the row from local state only
 * (currentUser.id, vars.candidate_id, vars.content, fresh ISO now). The
 * optimistic id is `crypto.randomUUID()` prefixed `"optimistic-"`.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useAuthStore } from "@/stores/useAuthStore";
import { useToastStore } from "@/stores/useToastStore";
import type { NoteOut, NotesPage } from "./useNotes";

interface CreateNoteVars {
  candidate_id: string;
  content: string;
  /** Phase 2.5: JD context ID for per-(candidate × JD) note scoping. */
  jd_id?: string;
}

interface OptimisticContext {
  /** Snapshot of the prior cache value, used for rollback in onError. */
  previous: InfiniteData<NotesPage, string | null> | undefined;
  /** The optimistic id we wrote — used for replace-by-id in onSuccess. */
  optimisticId: string;
}

function makeOptimisticId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `optimistic-${crypto.randomUUID()}`;
  }
  return `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useCreateNote(): UseMutationResult<
  NoteOut,
  ApiError,
  CreateNoteVars,
  OptimisticContext
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.userId);
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<NoteOut, ApiError, CreateNoteVars, OptimisticContext>({
    mutationFn: async (vars) => {
      const body: Record<string, string> = {
        candidate_id: vars.candidate_id,
        content: vars.content,
      };
      // Phase 2.5: pipe jd_id for per-(candidate × JD) scoping.
      if (vars.jd_id) body["jd_id"] = vars.jd_id;
      const r = await apiClient.post<NoteOut>(
        `/api/t/${tenantSlug}/notes`,
        body,
      );
      return r.data;
    },
    onMutate: async (vars) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;

      // Cancel any in-flight fetch so a stale GET doesn't overwrite our
      // optimistic write between onMutate and onSuccess.
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<
        InfiniteData<NotesPage, string | null>
      >(queryKey);

      const optimisticId = makeOptimisticId();

      const optimisticRow: NoteOut = {
        id: optimisticId,
        tenant_id: "", // never displayed
        user_id: currentUserId ?? "",
        candidate_id: vars.candidate_id,
        content: vars.content,
        created_at: new Date().toISOString(),
      };

      // AR-5 page-shape contract: prepend to pages[0].items only;
      // preserve pages.length, pageParams, next_cursor, has_more.
      // If pages is undefined/empty (cold cache), the updater is a no-op.
      queryClient.setQueryData<InfiniteData<NotesPage, string | null>>(
        queryKey,
        (prev) => {
          if (!prev || prev.pages.length === 0) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page, i) =>
              i === 0
                ? { ...page, items: [optimisticRow, ...page.items] }
                : page,
            ),
          };
        },
      );

      return { previous, optimisticId };
    },
    onSuccess: (realRow, vars, context) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;
      // Replace the optimistic row with the real row in pages[0].items.
      // If the cache is empty (defensive — cold-cache create), re-write a
      // single-page cache so the user still sees their note immediately.
      queryClient.setQueryData<InfiniteData<NotesPage, string | null>>(
        queryKey,
        (prev) => {
          if (!prev || prev.pages.length === 0) {
            return {
              pages: [
                { items: [realRow], next_cursor: null, has_more: false },
              ],
              pageParams: [null],
            };
          }
          return {
            ...prev,
            pages: prev.pages.map((page, i) =>
              i === 0
                ? {
                    ...page,
                    items: page.items.map((it) =>
                      it.id === context?.optimisticId ? realRow : it,
                    ),
                  }
                : page,
            ),
          };
        },
      );
    },
    onError: (err, vars, context) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;
      // Rollback to the snapshot. The composer also restores its
      // textarea content via the captured-content ref (composer-side
      // responsibility, not the hook's).
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      } else {
        queryClient.setQueryData(queryKey, undefined);
      }

      // Toast surface per HoD §8 error matrix.
      if (err instanceof ApiError) {
        if (err.status === 401 || err.status === 403) {
          addToast("Session expired. Sign in again.", "error");
          return;
        }
        if (err.status === 404) {
          addToast("Candidate not found. Refresh the page.", "error");
          return;
        }
        if (err.status === 429) {
          addToast(
            "Too many notes too fast. Try again in a moment.",
            "warning",
          );
          return;
        }
        if (err.status === 422) {
          addToast("Couldn't save note. Try again.", "error");
          return;
        }
      }
      // Generic 5xx / network fallback.
      addToast("Couldn't save note. Try again.", "error");
    },
  });
}
